import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Award,
  Users,
  Shield,
  Leaf,
  HeartHandshake,
  Sparkles,
} from "lucide-react";
import { Seo, breadcrumbSchema } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { useSiteContent } from "../lib/useSiteContent";
import { ServiceCTA } from "../components/ServiceCTA";

const PATH = "/about";

const VALUES = [
  {
    icon: Award,
    title: "Inventory-grade by default",
    description:
      "Every booking is delivered to AIIC checkout-report standard — not a generic domestic clean rebadged for tenancies.",
  },
  {
    icon: Users,
    title: "Employed, vetted teams",
    description:
      "Our cleaners are employed, paid London Living Wage and DBS-checked. No day labour, no unvetted subcontracting.",
  },
  {
    icon: Shield,
    title: "Insured to £5M",
    description:
      "Public liability and treatment cover on every job — protection for tenants, landlords and managing agents alike.",
  },
  {
    icon: Leaf,
    title: "Quietly eco-conscious",
    description:
      "Biodegradable, fragrance-free chemistry as standard. HEPA-filter vacuums and reusable microfibre wherever possible.",
  },
  {
    icon: HeartHandshake,
    title: "Honest pricing",
    description:
      "Fixed quotes, no surprise on-the-day charges, and a clear written guarantee if the work needs revisiting.",
  },
  {
    icon: Sparkles,
    title: "One contractor, one finish",
    description:
      "End of tenancy, carpet, oven and deep cleans all run by the same team — so the finish is consistent end-to-end.",
  },
];

const About = () => {
  const sc = useSiteContent("about", {
    eyebrow:    "About Us",
    h1:         "The London cleaning team built",
    highlight:  "around the inventory report",
    subheading: "Prime Tenancy Cleaning was founded to fix one specific problem: the gap between a \"domestic\" clean and what a London letting agent actually inspects against.",
    story_eyebrow: "Our Story",
    story_heading: "A cleaning company for London's rental market — not the other way around",
    story_p1: "Prime Tenancy Cleaning was started by a team with first-hand experience of London's rental cycle on both sides: as long-term tenants who lost deposits to checkout reports that felt unfair, and later as landlords frustrated by contractors who said the right things but did not deliver inventory-grade work.",
    story_p2: "We built the company we wished we had been able to book. A small, employed cleaning team trained specifically on the AIIC inventory standard, dispatched from a single London base, using the same checklist used by major letting agents to evaluate checkout reports. No franchise patchwork, no last-minute subcontractors, no shifting spec.",
    story_p3: "Today we serve tenants, landlords and letting agents across all 32 London boroughs. Most of our bookings come from agent referrals and word-of-mouth from tenants who recovered their full deposit on the first attempt. Read more about how each service works on the",
    values_eyebrow: "What We Stand For",
    values_heading: "The six principles every job runs on",
    values_sub:     "No marketing-speak. These are the operating rules our team actually works to, every single visit.",
    coverage_eyebrow: "Where We Work",
    coverage_heading: "All 32 London boroughs, Zone 1 to the M25",
    coverage_body:    "From Mayfair mansion blocks to Walthamstow HMOs, Greenwich townhouses to Wandsworth conversions — our teams cover every London postcode. Same-day jobs are available across central zones, with planned bookings in outer boroughs from £99.",
    cta_title: "Work with a London team built for inventories",
    cta_desc:  "Get a tailored quote in hours — or browse our service pages to see exactly what each clean includes.",
  });
  return (
    <>
      <Seo
        title="About — Prime Tenancy Cleaning London"
        description="About Prime Tenancy Cleaning — a specialist London move-out cleaning team serving tenants, landlords and letting agents across all 32 boroughs. Inventory-grade, insured, employed cleaners."
        ogTitle="About Prime Tenancy Cleaning London"
        ogDescription="A specialist London move-out cleaning company built around the inventory checkout report. Meet the team behind the re-clean guarantee."
        path={PATH}
        schema={[
          breadcrumbSchema([{ name: "About", path: PATH }]),
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "About Prime Tenancy Cleaning",
            url: "https://primetenancycleaning.com/about",
          },
        ]}
      />

      <PageHero
        eyebrow={sc.eyebrow}
        h1={sc.h1}
        highlight={sc.highlight}
        subheading={sc.subheading}
        breadcrumbs={[{ name: "About", path: PATH }]}
        image="https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=1920&q=80"
      />

      {/* Story */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 max-w-6xl mx-auto">
            <div className="lg:col-span-7">
              <span className="text-teal font-semibold text-sm uppercase tracking-wider">{sc.story_eyebrow}</span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mt-3 mb-6 leading-tight">{sc.story_heading}</h2>
              <div className="text-slate-700 text-base md:text-lg leading-relaxed space-y-5">
                <p>{sc.story_p1}</p>
                <p>{sc.story_p2}</p>
                <p>
                  {sc.story_p3}{" "}
                  <Link to="/end-of-tenancy-cleaning-london" className="text-teal font-medium hover:text-navy">end of tenancy</Link>
                  ,{" "}
                  <Link to="/deep-cleaning-london" className="text-teal font-medium hover:text-navy">deep cleaning</Link>
                  ,{" "}
                  <Link to="/carpet-cleaning-london" className="text-teal font-medium hover:text-navy">carpet cleaning</Link>{" "}
                  and{" "}
                  <Link to="/oven-cleaning-london" className="text-teal font-medium hover:text-navy">oven cleaning</Link>{" "}
                  pages.
                </p>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-navy rounded-2xl p-7 md:p-9 text-white sticky top-28">
                <h3 className="font-heading text-2xl font-semibold mb-5">
                  By the numbers
                </h3>
                <dl className="space-y-5">
                  {[
                    { k: "32", v: "London boroughs covered" },
                    { k: "£5M", v: "Public liability insurance per visit" },
                    { k: "7-day", v: "Re-clean guarantee on every end of tenancy job" },
                    { k: "7 days", v: "A week, including bank holidays" },
                  ].map((stat) => (
                    <div
                      key={stat.k}
                      className="flex items-baseline gap-4 border-b border-white/10 pb-4 last:border-b-0 last:pb-0"
                    >
                      <dt className="font-heading text-3xl font-bold text-gold w-20 flex-shrink-0">
                        {stat.k}
                      </dt>
                      <dd className="text-slate-300 text-sm leading-relaxed">
                        {stat.v}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <span className="text-teal font-semibold text-sm uppercase tracking-wider">{sc.values_eyebrow}</span>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-navy mt-3 mb-4">{sc.values_heading}</h2>
            <p className="text-slate-600 text-lg">{sc.values_sub}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <motion.div
                  key={v.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="bg-white rounded-2xl p-7 border border-slate-100"
                  data-testid={`value-card-${i}`}
                >
                  <div className="w-12 h-12 bg-teal/10 rounded-xl flex items-center justify-center mb-5">
                    <Icon className="w-6 h-6 text-teal" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-navy mb-2">
                    {v.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {v.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Coverage */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="max-w-4xl mx-auto text-center">
            <span className="text-teal font-semibold text-sm uppercase tracking-wider">{sc.coverage_eyebrow}</span>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-navy mt-3 mb-6 leading-tight">{sc.coverage_heading}</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-10">{sc.coverage_body}</p>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 text-sm">
              {[
                "Westminster",
                "Camden",
                "Islington",
                "Kensington & Chelsea",
                "Hammersmith & Fulham",
                "Wandsworth",
                "Lambeth",
                "Southwark",
                "Tower Hamlets",
                "Hackney",
                "Greenwich",
                "Lewisham",
                "Newham",
                "Waltham Forest",
                "Haringey",
                "Brent",
                "Ealing",
                "Hounslow",
                "Richmond",
                "Kingston",
                "Merton",
                "Sutton",
                "Croydon",
                "Bromley",
                "Barnet",
                "Enfield",
                "Redbridge",
                "Havering",
                "Bexley",
                "Barking & Dagenham",
                "Harrow",
                "Hillingdon",
              ].map((b) => (
                <span
                  key={b}
                  className="bg-slate-50 text-slate-700 py-2.5 px-3 rounded-lg font-medium"
                >
                  {b}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <ServiceCTA title={sc.cta_title} description={sc.cta_desc} variant="navy" />
    </>
  );
};

export default About;
